import React from 'react';
import { Box, Typography, Stack } from '@mui/material';

interface PageHeaderProps {
  title: string;
  description?: string;
  /** Small label above the title, e.g. the section a page belongs to. */
  eyebrow?: string;
  /** Primary + secondary actions, right-aligned on desktop. */
  actions?: React.ReactNode;
  children?: React.ReactNode;
}

/**
 * The single title block every page opens with. Keeping it in one component is
 * what makes headings, spacing and action placement identical across pages.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  eyebrow,
  actions,
  children,
}) => (
  <Box sx={{ mb: 3 }}>
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      alignItems={{ xs: 'stretch', md: 'flex-end' }}
      justifyContent="space-between"
    >
      <Box sx={{ minWidth: 0 }}>
        {eyebrow && (
          <Typography variant="overline" sx={{ color: 'primary.main', display: 'block', mb: 0.5 }}>
            {eyebrow}
          </Typography>
        )}
        <Typography variant="h3" component="h1" sx={{ mb: description ? 0.75 : 0 }}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: '68ch' }}>
            {description}
          </Typography>
        )}
      </Box>
      {actions && (
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0, flexWrap: 'wrap', gap: 1 }}>
          {actions}
        </Stack>
      )}
    </Stack>
    {children && <Box sx={{ mt: 2.5 }}>{children}</Box>}
  </Box>
);

export default PageHeader;
